/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import getToken from "../../../utils/getToken";
import { toAbsoluteUrl } from "../../../_flyingPaws/helpers";

const API_URL = process.env.REACT_APP_API_URL;

type AppIconVariant = "ICON_1" | "ICON_2";

const ICON_OPTIONS: {
  value: AppIconVariant;
  label: string;
  preview: string;
}[] = [
  { value: "ICON_1", label: "Icon 1", preview: "/media/app-icons/icon-1.png" },
  { value: "ICON_2", label: "Icon 2", preview: "/media/app-icons/icon-2.png" },
];

const cardStyle = { backgroundColor: "#000F1E" };

const AppIconWrapper: FC = () => {
  const [activeAppIcon, setActiveAppIcon] = useState<AppIconVariant>("ICON_1");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAppIcon = async () => {
      try {
        const token = getToken();
        const { data } = await axios({
          url: `${API_URL}/app-icon/all`,
          method: "get",
          headers: { Authorization: token },
        });
        const current = data?.data?.activeAppIcon;
        if (current === "ICON_1" || current === "ICON_2") {
          setActiveAppIcon(current);
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ?? "Failed to load app icon settings"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAppIcon();
  }, []);

  const save = async () => {
    const loadingToastId = toast.loading("Saving...");
    setSaving(true);
    try {
      const token = getToken();
      const config: AxiosRequestConfig = {
        url: `${API_URL}/app-icon/edit`,
        method: "patch",
        headers: { Authorization: token },
        data: { activeAppIcon },
      };
      await axios(config);
      toast.dismiss(loadingToastId);
      toast.success("App icon updated");
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      toast.error(error?.response?.data?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white p-10">Loading app icon settings…</div>;
  }

  return (
    <>
      <h1 className="text-white fw-bold mb-5">App Icon</h1>

      <div className="card border-0 mb-5" style={cardStyle}>
        <div className="card-header border-0 pt-6">
          <h3 className="card-title text-white fw-bold m-0">
            Active home-screen icon
          </h3>
        </div>

        <div className="card-body pt-3">
          <div className="row g-5">
            {ICON_OPTIONS.map((option) => {
              const selected = activeAppIcon === option.value;
              return (
                <div className="col-md-6" key={option.value}>
                  <div
                    onClick={() => setActiveAppIcon(option.value)}
                    className="h-100 d-flex flex-column align-items-center justify-content-center p-6 rounded"
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#FFFFFF",
                      border: selected
                        ? "3px solid #50CD89"
                        : "3px solid #2B3B52",
                    }}
                  >
                    <img
                      src={toAbsoluteUrl(option.preview)}
                      alt={option.label}
                      style={{
                        maxWidth: 160,
                        maxHeight: 160,
                        objectFit: "contain",
                        borderRadius: 24,
                      }}
                    />
                    <div className="form-check mt-5">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="activeAppIcon"
                        id={`icon-${option.value}`}
                        checked={selected}
                        onChange={() => setActiveAppIcon(option.value)}
                      />
                      <label
                        className="form-check-label fw-bold ms-2"
                        style={{ color: "#000F1E" }}
                        htmlFor={`icon-${option.value}`}
                      >
                        {option.label}
                        {selected ? " (selected)" : ""}
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-muted fs-8 mt-5">
            This switches the home-screen app icon among icons already shipped
            in the mobile build. Users pick it up the next time they open the
            app — no store release needed for a switch. Adding a new icon design
            requires a new mobile release. Independent from the in-app App Logo
            setting.
          </div>

          <div className="d-flex justify-content-end mt-5">
            <button className="btn btn-success" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export { AppIconWrapper };
export default AppIconWrapper;
